import { Grid2, Grid2 as Grid } from "@mui/material";
import StringInputField from "@/lib/components/inputfields/string-input-field";
import { useModelState } from "@/lib/modelstate";
import { authenticationApi } from "@/boxhub-api";
import { Language, UserInvitationDTO } from "@/api/data-contracts";
import * as React from "react";
import { useMemo } from "react";
import SelectInputField from "@/lib/components/inputfields/select-input-field";
import { languageOptions, salutationOptions, useAvailableLanguages } from "@/datacaches";
import { Constants } from "@/api/constants";
import { useTranslation } from "react-i18next";
import { CommonI18N, UserI18N } from "@/translations";
import { DialogEx } from "@/lib/components/DialogEx";
import { ButtonEx } from "@/lib/components/buttons/ButtonEx";
import { useUserInvitationSchema } from "@/pages/company-page/userInvitationSchema";
import { toLanguageSelectOptions } from "@/pages/user-page/languageSelectOptions";

export type UserInvitationDialogProps = {
    open: boolean;
    close: () => void;
    onSuccess?: () => void;
    /** The company to invite the user into. */
    companyId: number;
};

export const UserInvitationDialog = ({ open, close, onSuccess, companyId }: UserInvitationDialogProps) => {
    const { t } = useTranslation();
    const salutationSelectOptions = useMemo(
        () =>
            salutationOptions.map((option) => ({
                label: option && t(CommonI18N.enum.salutation[option]),
                value: option
            })),
        [t]
    );

    const [availableLanguages] = useAvailableLanguages();
    // While `useAvailableLanguages()` is loading (or permanently, on a fetch error) it resolves
    // to `[]` — fall back to the static baseline so the dropdown isn't empty and the schema's
    // `.oneOf(...)` (see userInvitationSchema.ts) never validates against an empty set.
    const effectiveLanguages = availableLanguages.length > 0 ? availableLanguages : languageOptions;
    const languageSelectOptions = useMemo(
        () => toLanguageSelectOptions(effectiveLanguages, t),
        [effectiveLanguages, t]
    );
    const schema = useUserInvitationSchema(effectiveLanguages);
    const { model, errors, onChange, save } = useModelState<UserInvitationDTO>({
        initialState: { language: Language.GERMAN } as UserInvitationDTO,
        schema,
        saver: (data: UserInvitationDTO) => authenticationApi.generateUserSignUpInvitation({ ...data, companyId })
    });

    const handleInvite = async () => {
        // `save()` returns a Promise only when client-side validation passed; on a validation
        // error it returns undefined and the dialog must stay open. Preserve that guard.
        const savePromise = save();
        if (savePromise == null) {
            return;
        }
        // `save()` reports a failed request via its own saveFailed snackbar and re-throws, so this
        // handler must NOT catch: awaiting means close()/onSuccess run only after a successful save,
        // and the dialog stays open on error without showing a duplicate snackbar.
        await savePromise;
        close();
        onSuccess?.();
    };

    const buttons = (
        <>
            <Grid2>
                <ButtonEx label={t(CommonI18N.action.cancel)} color="secondary" onClick={close} />
            </Grid2>
            <Grid2>
                <ButtonEx
                    label={t(UserI18N.form.action.inviteEmployee)}
                    color="primary"
                    disabled={model.email == null}
                    onClick={handleInvite}
                />
            </Grid2>
        </>
    );

    return (
        <DialogEx
            title={t(UserI18N.form.page.inviteTitle)}
            open={open}
            onClose={close}
            maxWidth="sm"
            fullWidth
            buttons={buttons}
        >
            <Grid container spacing={2} padding={2} width="100%">
                <Grid width="100%">
                    <StringInputField
                        name="email"
                        value={model?.email}
                        label={t(UserI18N.form.field.email)}
                        error={errors?.email}
                        maxChars={Constants.EMAIL_LENGTH_MAX}
                        onChange={onChange}
                    />
                </Grid>
                <Grid width="100%">
                    <SelectInputField
                        name="salutation"
                        label={t(UserI18N.form.field.salutation)}
                        value={model?.salutation}
                        error={errors?.salutation}
                        options={salutationSelectOptions}
                        displayEmpty
                        onChange={onChange}
                    />
                </Grid>
                <Grid width="100%">
                    <StringInputField
                        name="firstName"
                        label={t(UserI18N.form.field.firstName)}
                        value={model?.firstName}
                        error={errors?.firstName}
                        maxChars={Constants.FIRSTNAME_LENGTH_MAX}
                        onChange={onChange}
                    />
                </Grid>
                <Grid width="100%">
                    <StringInputField
                        name="lastName"
                        label={t(UserI18N.form.field.lastName)}
                        value={model?.lastName}
                        error={errors?.lastName}
                        maxChars={Constants.LASTNAME_LENGTH_MAX}
                        onChange={onChange}
                    />
                </Grid>
                <Grid width="100%">
                    <StringInputField
                        name="phone"
                        label={t(UserI18N.form.field.phone)}
                        value={model?.phone}
                        error={errors?.phone}
                        maxChars={Constants.PHONE_NUMBER_LENGTH_MAX}
                        onChange={onChange}
                    />
                </Grid>
                <Grid width="100%">
                    <StringInputField
                        name="fax"
                        label={t(UserI18N.form.field.fax)}
                        value={model?.fax}
                        error={errors?.fax}
                        maxChars={Constants.PHONE_NUMBER_LENGTH_MAX}
                        onChange={onChange}
                    />
                </Grid>
                <Grid width="100%">
                    <SelectInputField
                        name="language"
                        label={t(UserI18N.form.field.language)}
                        value={model?.language}
                        error={errors?.language}
                        options={languageSelectOptions}
                        displayEmpty
                        onChange={onChange}
                    />
                </Grid>
            </Grid>
        </DialogEx>
    );
};
